import { Big } from 'hypo-shared-utils';
import {
  FEE_SCHEDULE_TYPES,
  FRONT_LOAD_TYPES,
  FRONT_LOAD_AMOUNT_TYPES,
  FRONT_LOAD_FEE_TYPE_OPTIONS,
} from './constants';

export {
  autoCalculateHighValue,
  isSMAManagerFeeExcluded,
} from 'hypo-fee-schedule-summary-list/src/utils';

const { FLAT_MAX_FEE, HISTORIC_MAX_FEE } = FEE_SCHEDULE_TYPES;
const { STANDARD, CUSTOM, LOI, VAH } = FRONT_LOAD_TYPES;
const { AMOUNT, PERCENT } = FRONT_LOAD_AMOUNT_TYPES;

export const getFeeChoiceLabelKey = feeScheduleLabelType => {
  const feeChoiceLabelMap = {
    0: 'annualAdvisoryFee',
    1: 'annualMaxFeePercent',
    2: 'feeSchedule',
    3: 'annualMaxFeeAndFeeSchedule',
  };
  return feeChoiceLabelMap[feeScheduleLabelType];
};

export const getAnnualFeeTypeLabelKey = feeScheduleLabelType => {
  const feeChoiceLabelMap = {
    0: 'annualAdvisoryFee',
    1: 'maxFee',
    2: 'feeSchedule',
    3: 'maxFeeAndFeeSchedule',
  };
  return feeChoiceLabelMap[feeScheduleLabelType];
};

export const frontLoadTypeDataMapping = frontLoadFeeType => {
  const selectedFeesOption = Object.entries(FRONT_LOAD_FEE_TYPE_OPTIONS).find(
    option => option[1] === frontLoadFeeType
  );
  if (!selectedFeesOption) {
    return null;
  }
  const mapping = {
    STANDARD: {
      frontLoadType: STANDARD,
    },
    CUSTOM_AMOUNT: {
      frontLoadType: CUSTOM,
      frontLoadAmountType: AMOUNT,
    },
    CUSTOM_PERCENT: {
      frontLoadType: CUSTOM,
      frontLoadAmountType: PERCENT,
    },
    LOI: {
      frontLoadType: LOI,
    },
    VAH: {
      frontLoadType: VAH,
    },
  };
  return mapping[selectedFeesOption[0]];
};

export const isNumber = str => {
  const reg = /^-?[\d,]+(.[\d]+)?$/; // match the numbers such as '123', '-123', '123.00', '123,456.00'
  return reg.test(str);
};

export const roundNumber = (number, precision = 2) => {
  // use number exponential notation to avoid 1.005 round to 1
  const precisionNumber = Number(`${number}e${precision}`);
  return Math.round(precisionNumber) / 10 ** precision;
};

export const parseNumber = (str, decimal = 2) => {
  // keep 2 decimal places
  if (isNumber(str)) {
    const num = parseFloat(str.replace(/,/g, ''));
    return roundNumber(num, decimal);
  }
  return NaN;
};

export const isInvalidAmount = num => {
  return Number.isNaN(num) || num < 0;
};

export const isInvalidPercentage = num => {
  return Number.isNaN(num) || num < 0 || num > 100;
};

export const renderEmDash = () => {
  const div = document.createElement('div');
  div.innerHTML = '&mdash;';
  return div.innerHTML;
};

export function isObjEmpty(obj) {
  return Object.keys(obj).length === 0;
}

export function omit(obj, keys) {
  return keys.reduce((memo, key) => {
    const { [key]: ignored, ...rest } = memo;
    return rest;
  }, obj);
}

function roundFloatNumber(num, decimalDigits = 2) {
  return Big(num)
    .round(decimalDigits)
    .toNumber();
}

function compareFloatNumber(a, b, decimalDigits = 2) {
  const ra = roundFloatNumber(a, decimalDigits);
  const rb = roundFloatNumber(b, decimalDigits);
  return Big(ra).cmp(rb);
}

export function greaterThanOrEqual(a, b, decimalDigits = 2) {
  const result = compareFloatNumber(a, b, decimalDigits);
  return result === 1 || result === 0;
}

function lessThanOrEqual(a, b, decimalDigits = 2) {
  const result = compareFloatNumber(a, b, decimalDigits);
  return result === -1 || result === 0;
}

export function equal(a, b, decimalDigits = 2) {
  const result = compareFloatNumber(a, b, decimalDigits);
  return result === 0;
}

export function isPercentageInRange(percentage, lowRange = 0, highRange = 100) {
  return greaterThanOrEqual(percentage, lowRange, 4) && lessThanOrEqual(percentage, highRange, 4);
}

export const isMaxFee = feeSchedule => {
  return feeSchedule?.isMaxFee || feeSchedule?.feeScheduleType === FLAT_MAX_FEE;
};

export const isHistoricMaxFee = feeSchedule => {
  return feeSchedule?.isHistoricMaxFee || feeSchedule?.feeScheduleType === HISTORIC_MAX_FEE;
};

export const isFlatOrHistoricMaxFee = feeSchedule => {
  return isMaxFee(feeSchedule) || isHistoricMaxFee(feeSchedule);
};

export const isNullOrUndefined = value => {
  return value === undefined || value === null;
};

export const addFloatNumber = (numbers, digit = 2) => {
  const scale = 10 ** digit;
  return numbers.reduce((sum, number) => sum + number * scale, 0) / scale;
};
